import { useState, type JSX } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  MessageCircle,
  Phone,
  Bell as BellIcon,
  CircleAlert,
  CircleCheck,
  Send,
  Eye,
  MousePointerClick,
  CircleX,
} from 'lucide-react';
import type {
  NotificationChannel,
  NotificationLogItem,
  NotificationLogQuery,
  NotificationStatus,
} from '@caratuva/shared-types';
import { listNotifications } from '../lib/api';

export const Route = createFileRoute('/_app/notifications')({
  component: NotificationsLogPage,
});

const CHANNELS: readonly NotificationChannel[] = ['email', 'inApp'];
const STATUSES: readonly NotificationStatus[] = [
  'sent',
  'delivered',
  'opened',
  'clicked',
  'bounced',
  'failed',
  'rate_limited',
  'template_error',
];

function NotificationsLogPage(): JSX.Element {
  const [filters, setFilters] = useState<Partial<NotificationLogQuery>>({ limit: 50 });
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const queryArgs: Partial<NotificationLogQuery> = { ...filters, ...(cursor ? { cursor } : {}) };

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications.log', queryArgs],
    queryFn: ({ signal }) => listNotifications(queryArgs, signal),
  });

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold tracking-tight text-navy-900">Notifications</h1>
      <p className="mb-6 max-w-prose text-sm text-navy-600">
        Every email and in-app notification dispatched on behalf of your organization. Delivery
        state updates as it is reported back.
      </p>

      <div className="mb-4 flex flex-wrap gap-3">
        <select
          aria-label="Channel"
          className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm"
          value={filters.channel ?? ''}
          onChange={(e): void => {
            const next: Partial<NotificationLogQuery> = { ...filters };
            if (e.target.value === '') delete next.channel;
            else next.channel = e.target.value as NotificationChannel;
            setFilters(next);
            setCursor(undefined);
          }}
        >
          <option value="">All channels</option>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm"
          value={filters.status ?? ''}
          onChange={(e): void => {
            const next: Partial<NotificationLogQuery> = { ...filters };
            if (e.target.value === '') delete next.status;
            else next.status = e.target.value as NotificationStatus;
            setFilters(next);
            setCursor(undefined);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="date"
          aria-label="From"
          className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm"
          value={filters.from?.slice(0, 10) ?? ''}
          onChange={(e): void => {
            const next: Partial<NotificationLogQuery> = { ...filters };
            if (e.target.value === '') delete next.from;
            else next.from = `${e.target.value}T00:00:00.000Z`;
            setFilters(next);
            setCursor(undefined);
          }}
        />
        <input
          type="date"
          aria-label="To"
          className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm"
          value={filters.to?.slice(0, 10) ?? ''}
          onChange={(e): void => {
            const next: Partial<NotificationLogQuery> = { ...filters };
            if (e.target.value === '') delete next.to;
            else next.to = `${e.target.value}T23:59:59.999Z`;
            setFilters(next);
            setCursor(undefined);
          }}
        />
      </div>

      {isLoading && <p className="text-sm text-navy-500">Loading…</p>}
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error instanceof Error ? error.message : 'Failed to load notifications'}
        </p>
      )}

      {data && (
        <>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-navy-200 text-left text-navy-600">
                <th className="py-2 pr-4 font-medium">Sent</th>
                <th className="py-2 pr-4 font-medium">Channel</th>
                <th className="py-2 pr-4 font-medium">Template</th>
                <th className="py-2 pr-4 font-medium">Recipient</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Reference</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <NotificationRow key={row.id} row={row} />
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-navy-500">
                    No notifications match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm disabled:opacity-50"
              onClick={(): void => {
                setCursor(undefined);
              }}
              disabled={!cursor}
            >
              First page
            </button>
            <button
              type="button"
              className="rounded-md border border-navy-300 bg-white px-3 py-1.5 text-sm disabled:opacity-50"
              onClick={(): void => {
                if (data.nextCursor) setCursor(data.nextCursor);
              }}
              disabled={!data.nextCursor}
            >
              Next page
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function NotificationRow({ row }: { row: NotificationLogItem }): JSX.Element {
  return (
    <tr className="border-b border-navy-100 align-top">
      <td className="py-2 pr-4 text-navy-700">{new Date(row.sentAt).toLocaleString()}</td>
      <td className="py-2 pr-4 text-navy-700">
        <ChannelLabel channel={row.channel} />
      </td>
      <td className="py-2 pr-4 text-navy-700">
        {row.templateId}
        <span className="text-navy-400"> · v{row.templateVersion}</span>
      </td>
      <td className="py-2 pr-4 text-navy-700">
        {row.recipientEmail ?? maskPhone(row.recipientPhone)}
      </td>
      <td className="py-2 pr-4 text-navy-700">
        <StatusChip status={row.status} reason={row.failureReason} />
      </td>
      <td className="py-2 pr-4 text-navy-600">
        {row.correlationEntityType && row.correlationEntityId
          ? `${row.correlationEntityType} ${row.correlationEntityId}`
          : '—'}
      </td>
    </tr>
  );
}

function ChannelLabel({ channel }: { channel: NotificationChannel }): JSX.Element {
  const Icon =
    channel === 'email'
      ? Mail
      : channel === 'whatsapp'
        ? MessageCircle
        : channel === 'sms'
          ? Phone
          : BellIcon;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon aria-hidden className="h-4 w-4" strokeWidth={1.5} />
      {channel}
    </span>
  );
}

function StatusChip({
  status,
  reason,
}: {
  status: NotificationStatus;
  reason: string | null;
}): JSX.Element {
  const { Icon, color } = pickStatusVisual(status);
  return (
    <span
      title={reason ?? undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${color}`}
    >
      <Icon aria-hidden className="h-3 w-3" strokeWidth={1.5} />
      {status}
    </span>
  );
}

function pickStatusVisual(status: NotificationStatus): {
  Icon: typeof Send;
  color: string;
} {
  switch (status) {
    case 'sent':
      return { Icon: Send, color: 'bg-navy-100 text-navy-700' };
    case 'delivered':
      return { Icon: CircleCheck, color: 'bg-emerald-50 text-emerald-700' };
    case 'opened':
      return { Icon: Eye, color: 'bg-emerald-50 text-emerald-700' };
    case 'clicked':
      return { Icon: MousePointerClick, color: 'bg-emerald-50 text-emerald-700' };
    case 'bounced':
    case 'failed':
      return { Icon: CircleX, color: 'bg-red-50 text-red-700' };
    case 'rate_limited':
    case 'template_error':
      return { Icon: CircleAlert, color: 'bg-amber-50 text-amber-700' };
  }
}

function maskPhone(phone: string | null): string {
  if (!phone) return '—';
  if (phone.length <= 4) return phone;
  return `${phone.slice(0, 3)}…${phone.slice(-4)}`;
}
